import React from 'react';
import { Card, Dropdown } from '@bynder/design-system';
import { IconAdd, IconDelete, IconEdit, IconMore, IconStateIndicatorM } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { PartialComponent } from 'packages/services/componentService';
import { useFriendlyTime } from '~/hooks/useFriendlyTime';
import { StyledInlineBanner, StyledSectionItem, StyledSectionTitle } from './styled';

interface Props {
    component: PartialComponent;
    collectionName: string;
    onApply: (isUniqueContentProperties: boolean) => void;
    onEdit: () => void;
    onDelete: () => void;
    onOpen: () => void;
    onClose: () => void;
    isDisabled?: boolean;
}

const ComponentMenu = ({
    component,
    collectionName,
    onApply,
    onEdit,
    onDelete,
    onOpen,
    onClose,
    isDisabled = false,
}: Props) => {
    const { translate } = useTranslate();
    const { getFriendlyTime } = useFriendlyTime();

    return (
        <Dropdown
            onOpen={onOpen}
            onClose={onClose}
            noMaxHeight
            trigger={({ isOpen, ...triggerProps }) => (
                <Card.ContextActionButton
                    aria-label={translate('components.overview.card.menu')}
                    isPressed={isOpen}
                    {...triggerProps}
                >
                    <IconMore />
                </Card.ContextActionButton>
            )}
        >
            {isDisabled && (
                <StyledInlineBanner>{translate('components.overview.card.menu.videoIncompatible')}</StyledInlineBanner>
            )}
            <StyledSectionTitle>{component.name}</StyledSectionTitle>
            {component.description && <StyledSectionItem>{component.description}</StyledSectionItem>}
            <StyledSectionItem>
                <IconStateIndicatorM size="24" />
                {collectionName}
            </StyledSectionItem>
            <StyledSectionItem>
                <IconStateIndicatorM size="24" />
                {translate('components.overview.card.menu.created', { date: getFriendlyTime(component.created) })}
            </StyledSectionItem>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => onApply(true)} icon={<IconAdd />} isDisabled={isDisabled}>
                {translate('components.overview.card.menu.uniqueContentProperties')}
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => onEdit()} icon={<IconEdit />}>
                {translate('components.overview.card.menu.editDetails')}
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => onDelete()} icon={<IconDelete />}>
                {translate('components.overview.card.menu.delete')}
            </Dropdown.Item>
        </Dropdown>
    );
};

export default ComponentMenu;
